import React, { createContext, useContext, useState } from "react";
import creditNoteService from "../api/services/creditNoteService";
import { useAuth } from "./AuthContext";
import { useFetchOncePerUser } from "../hooks/useFetchOncePerUser";

// Create context
const CreditNoteContext = createContext();

// Custom hook for using this context
export const useCreditNote = () => useContext(CreditNoteContext);

// Provider component
export const CreditNoteProvider = ({ children }) => {
  const { user } = useAuth();
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load all credit notes
  async function loadCreditNotes() {
    setLoading(true);
    setError(null);
    try {
      const data = await creditNoteService.getAllCreditNotes();
      setCreditNotes(data);
    } catch (error) {
      // Users without sales access are denied; that is not a failure.
      if (error?.requiredPermission || /^access denied/i.test(error?.error || "")) {
        setCreditNotes([]);
      } else {
        console.error("Error loading credit notes:", error);
        setError("Failed to load credit notes. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }
  useFetchOncePerUser(user, loadCreditNotes);

  // Get credit note by ID
  const getCreditNote = async (id) => {
    setLoading(true);
    setError(null);
    try {
      return await creditNoteService.getCreditNoteById(id);
    } catch (error) {
      console.error(`Error getting credit note with id ${id}:`, error);
      setError("Failed to load credit note. Please try again later.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Create a new credit note
  const createCreditNote = async (creditNoteData) => {
    setLoading(true);
    setError(null);
    try {
      const newCreditNote = await creditNoteService.createCreditNote(
        creditNoteData
      );
      setCreditNotes([...creditNotes, newCreditNote]);
      return newCreditNote;
    } catch (error) {
      console.error("Error creating credit note:", error);
      setError("Failed to create credit note. Please try again later.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing credit note
  const updateCreditNote = async (id, creditNoteData) => {
    setLoading(true);
    setError(null);
    try {
      const updatedCreditNote = await creditNoteService.updateCreditNote(
        id,
        creditNoteData
      );
      setCreditNotes(
        creditNotes.map((note) => (note._id === id ? updatedCreditNote : note))
      );
      return updatedCreditNote;
    } catch (error) {
      console.error(`Error updating credit note with id ${id}:`, error);
      setError("Failed to update credit note. Please try again later.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a credit note
  const deleteCreditNote = async (id) => {
    setLoading(true);
    setError(null);
    try {
      await creditNoteService.deleteCreditNote(id);
      setCreditNotes(creditNotes.filter((note) => note._id !== id));
    } catch (error) {
      console.error(`Error deleting credit note with id ${id}:`, error);
      setError("Failed to delete credit note. Please try again later.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Void a credit note
  const voidCreditNote = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const voidedNote = await creditNoteService.voidCreditNote(id);
      setCreditNotes(
        creditNotes.map((note) => (note._id === id ? voidedNote : note))
      );
      return voidedNote;
    } catch (error) {
      console.error(`Error voiding credit note with id ${id}:`, error);
      setError("Failed to void credit note. Please try again later.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get credit notes by customer
  const getCreditNotesByCustomer = async (customerId) => {
    setLoading(true);
    setError(null);
    try {
      return await creditNoteService.getCreditNotesByCustomer(customerId);
    } catch (error) {
      console.error(
        `Error getting credit notes for customer ${customerId}:`,
        error
      );
      setError("Failed to load customer credit notes. Please try again later.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Apply credit note to invoice
  const applyCreditNote = async (creditNoteId, invoiceId, amount) => {
    setLoading(true);
    setError(null);
    try {
      const result = await creditNoteService.applyCreditNote(
        creditNoteId,
        invoiceId,
        amount
      );

      // Update the credit note in the state if it was returned
      const updated = result.creditNote || result.data;
      if (updated) {
        setCreditNotes(
          creditNotes.map((note) =>
            note._id === creditNoteId ? updated : note
          )
        );
      }

      return result;
    } catch (error) {
      console.error(
        `Error applying credit note ${creditNoteId} to invoice:`,
        error
      );
      setError("Failed to apply credit note. Please try again later.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    creditNotes,
    loading,
    error,
    loadCreditNotes,
    getCreditNote,
    createCreditNote,
    updateCreditNote,
    deleteCreditNote,
    voidCreditNote,
    getCreditNotesByCustomer,
    applyCreditNote,
  };

  return (
    <CreditNoteContext.Provider value={value}>
      {children}
    </CreditNoteContext.Provider>
  );
};

export default CreditNoteContext;
